import { useState, useEffect, useCallback } from 'react';
import { Star, Send } from 'lucide-react';
import Button from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { fetchEstablishmentReviews, reviewAuthorDisplayName, type ReviewWithAuthor } from '../utils/reviewDisplay';

interface ReviewsSectionProps {
    establishmentId: string;
    isAuthenticated: boolean;
}

const ReviewsSection: React.FC<ReviewsSectionProps> = ({ establishmentId, isAuthenticated }) => {
    const { user } = useAuth();
    const [reviews, setReviews] = useState<ReviewWithAuthor[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const fetchReviews = useCallback(async () => {
        try {
            const viewer =
                user != null
                    ? { id: user.id, name: user.name, avatar: user.avatar ?? null }
                    : undefined;
            const rows = await fetchEstablishmentReviews(supabase, establishmentId, { viewer });
            setReviews(rows);
        } catch {
            // silently fail
        } finally {
            setLoading(false);
        }
    }, [establishmentId, user]);

    useEffect(() => {
        void fetchReviews();
    }, [fetchReviews]);

    const handleSubmit = async () => {
        if (!isAuthenticated) {
            setError('Faça login para avaliar');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const stored = localStorage.getItem('user');
            if (!stored) throw new Error('Faça login para avaliar');
            const user = JSON.parse(stored);

            const { error: insertError } = await supabase.rpc('insert_review', {
                p_establishment_id: establishmentId,
                p_user_id: user.id,
                p_rating: rating,
                p_comment: comment || null,
            });

            if (insertError) throw new Error(insertError.message);

            await fetchReviews();
            setShowForm(false);
            setComment('');
            setRating(5);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Erro ao enviar avaliação');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const renderStars = (count: number, interactive = false) => {
        return (
            <div className="stars-container">
                {[1, 2, 3, 4, 5].map(i => (
                    <Star
                        key={i}
                        size={interactive ? 28 : 16}
                        fill={i <= count ? '#FFD700' : 'transparent'}
                        color="#FFD700"
                        style={interactive ? { cursor: 'pointer' } : {}}
                        onClick={interactive ? () => setRating(i) : undefined}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="reviews-section">
            <div className="reviews-header">
                <h3><Star size={18} /> Avaliações ({reviews.length})</h3>
                {isAuthenticated && (
                    <Button variant="outline" onClick={() => setShowForm(!showForm)}>
                        Avaliar
                    </Button>
                )}
            </div>

            {showForm && (
                <div className="review-form">
                    <p>Sua avaliação:</p>
                    {renderStars(rating, true)}
                    <textarea
                        placeholder="Escreva um comentário (opcional)"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                    />
                    {error && <p className="error-text">{error}</p>}
                    <Button onClick={handleSubmit} disabled={submitting}>
                        <Send size={16} /> {submitting ? 'Enviando...' : 'Enviar Avaliação'}
                    </Button>
                </div>
            )}

            {loading ? (
                <p>Carregando avaliações...</p>
            ) : reviews.length === 0 ? (
                <p className="no-reviews">Ainda não há avaliações. Seja o primeiro!</p>
            ) : (
                <div className="reviews-list">
                    {reviews.map(review => (
                        <div key={review.id} className="review-card">
                            <div className="review-header">
                                <span className="review-user">{reviewAuthorDisplayName(review)}</span>
                                {renderStars(review.rating)}
                            </div>
                            {review.comment && <p className="review-comment">{review.comment}</p>}
                            <span className="review-date">{formatDate(review.createdAt)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReviewsSection;
